import { get, identity, intersection } from 'lodash'
import { Observable, from, iif, of } from 'rxjs'
import { filter, first, map, mergeMap, pluck, switchMap, take, tap, toArray } from 'rxjs/operators'

import { Translation } from '@skinsoft/types'
import { Proxy } from '@skinsoft/network'
import { dbCacheDown } from '@skinsoft/offline/dexie/cache-down'
import { DataStore } from '@skinsoft/data-store'

// Use dbCacheDown directly instead of a TranslationOfflineDataFetcher because Translation has no id.
// It was done quickly, and it needs to use a dedicated TranslationOfflineDataFetcher to be done cleanly.
// @ts-ignore
export class TranslationDataStore extends DataStore<Translation> {
    public static getInstance() {
        if (!TranslationDataStore.instance) {
            TranslationDataStore.instance = new TranslationDataStore();
        }

        return TranslationDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$translationDataStore = TranslationDataStore.getInstance();
    }

    private static instance: TranslationDataStore = null as any;

    constructor(proxy?: Proxy) {
        super('/translations', undefined, undefined, proxy)
    }

    protected initializeDataFetcher() {
        //throw new Error('Method not implemented.');
    }

    protected initializeDataPusher() {
        //throw new Error("Method not implemented.");
    }

    public fetchOne(id: string, networkFirst = false) {
        return super.fetchOne(id, false).pipe(
            tap(translation => {
                dbCacheDown.translation.put([translation], id)
            })
        )
    }

    public getMessage(key: string, locale: string): Observable<string> {
        return from(dbCacheDown.translation.get(locale))
            .pipe(
                map(
                    translations => {
                        if (translations && translations[0]) {
                            return get(translations[0].message, key);
                        }
                        return key;
                    }
                )
            )
    }

    public getAllAvailableLanguages(languageList: string[] = []): Observable<string[]> {
        return this.proxy.fetch('/languages').pipe(
            pluck('_embedded', 'languages'),
            map(languages => languages.map(language => language.languageKey)),
            map(appLanguages => 
                iif(
                    () => languageList.length === 0, 
                    of(appLanguages), 
                    of(intersection(appLanguages, languageList))
                )
            ),
            switchMap(languages => languages),
            first(),
        )
    }

    public filterLanguageListWithCacheContent(languageList: string[]): Observable<string[]> {
        return from(languageList).pipe(
            mergeMap(locale => {
                return dbCacheDown.translation.get(locale)
            }),
            take(languageList.length),
            map(list => list && list[0]),
            filter(identity),
            map(translation => translation?.lang || ''),
            toArray()
        )
    }

    public getDateFormat(): Observable<String> {
        return this.proxy.fetch('/languages/formatDateEnUs')
    }
}
